import DashboardService from "@/src/api/DashboardService.js";
import AppStoreService from "@/src/api/AppStoreService.js";
import WorkspaceService from "@/src/api/WorkspaceService.js";
import { getCountryCodeFromDialCode } from "@/src/utils/phoneNumberUtils.js";

const USER_COUNTRY_CODE_KEY = "user_default_country_code";

let fetchTimezonePromise = null;

export default {
  namespaced: true,

  state: {
    isPhoneNumberVerified: false,
    userTimezone: null,
    timezoneInitialized: false,
    phoneNumbers: [],
    workspaceDefaultCountryCode: "US",
    workspaceDefaultDialCode: "+1",
    defaultCountryCode: "US",
    defaultCountryCodeInitialized: false,
  },

  mutations: {
    SET_IS_PHONE_NUMBER_VERIFIED(state, value) {
      state.isPhoneNumberVerified = value;
    },

    SET_USER_TIMEZONE(state, timezone) {
      state.userTimezone = timezone;
      state.timezoneInitialized = true;
    },

    SET_PHONE_NUMBERS(state, value) {
      state.phoneNumbers = value;
    },

    SET_WORKSPACE_DEFAULT_COUNTRY_CODE(state, countryCode) {
      state.workspaceDefaultCountryCode = countryCode || "US";
      state.defaultCountryCodeInitialized = true;
    },

    SET_WORKSPACE_DEFAULT_DIAL_CODE(state, dialCode) {
      state.workspaceDefaultDialCode = dialCode || "+1";
    },

    SET_DEFAULT_COUNTRY_CODE(state, countryCode) {
      state.defaultCountryCode = countryCode || state.workspaceDefaultCountryCode;
    },
  },

  actions: {
    setIsPhoneNumberVerified({ commit }, value) {
      commit("SET_IS_PHONE_NUMBER_VERIFIED", value);
    },

    setPhoneNumbers({ commit }, value) {
      commit("SET_PHONE_NUMBERS", value);
    },

    async fetchUserTimezone({ commit, state }) {
      if (state.timezoneInitialized) {
        return state.userTimezone;
      }

      if (fetchTimezonePromise) {
        return fetchTimezonePromise;
      }

      fetchTimezonePromise = DashboardService.getProfile()
        .then((response) => {
          if (response?.success && response?.profile?.timezone) {
            commit("SET_USER_TIMEZONE", response.profile.timezone);
            return response.profile.timezone;
          } else {
            commit("SET_USER_TIMEZONE", null);
            return null;
          }
        })
        .catch((error) => {
          console.error("Error fetching user timezone:", error);
          commit("SET_USER_TIMEZONE", null);
          return null;
        })
        .finally(() => {
          fetchTimezonePromise = null;
        });

      return fetchTimezonePromise;
    },
    async refreshUserTimezone({ dispatch, state }) {
      state.timezoneInitialized = false;
      return await dispatch("fetchUserTimezone");
    },

    async fetchDefaultCountryCode({ commit, state }) {
      if (state.defaultCountryCodeInitialized) {
        return state.defaultCountryCode;
      }

      const savedUserCode = localStorage.getItem(USER_COUNTRY_CODE_KEY);

      try {
        const workspaceId = WorkspaceService.getCurrentWorkspaceId();
        if (!workspaceId) {
          commit("SET_WORKSPACE_DEFAULT_COUNTRY_CODE", "US");
          commit("SET_WORKSPACE_DEFAULT_DIAL_CODE", "+1");
          commit("SET_DEFAULT_COUNTRY_CODE", savedUserCode || "US");
          return savedUserCode || "US";
        }

        const response = await AppStoreService.getWorkspaceConfigurationByKey(
          workspaceId,
          "DEFAULT_COUNTRY_CODE"
        );

        const workspaceCode = getCountryCodeFromDialCode(response?.value);
        commit("SET_WORKSPACE_DEFAULT_COUNTRY_CODE", workspaceCode);
        commit("SET_WORKSPACE_DEFAULT_DIAL_CODE", response?.value || "+1");
        commit("SET_DEFAULT_COUNTRY_CODE", savedUserCode || workspaceCode);
        return savedUserCode || workspaceCode;
      } catch (error) {
        console.error("Error fetching default country code:", error);
        commit("SET_WORKSPACE_DEFAULT_COUNTRY_CODE", "US");
        commit("SET_WORKSPACE_DEFAULT_DIAL_CODE", "+1");
        commit("SET_DEFAULT_COUNTRY_CODE", savedUserCode || "US");
        return savedUserCode || "US";
      }
    },

    async updateWorkspaceDefaultCountryCode({ commit, state }, dialCode) {
      const normalized = dialCode?.startsWith("+") ? dialCode : `+${dialCode}`;
      const response = await AppStoreService.updateDefaultCountryCode(normalized);
      if (response?.success) {
        const workspaceCode = getCountryCodeFromDialCode(normalized);
        commit("SET_WORKSPACE_DEFAULT_COUNTRY_CODE", workspaceCode);
        commit("SET_WORKSPACE_DEFAULT_DIAL_CODE", normalized);
        const savedUserCode = localStorage.getItem(USER_COUNTRY_CODE_KEY);
        if (!savedUserCode) {
          commit("SET_DEFAULT_COUNTRY_CODE", workspaceCode);
        }
      }
      return response;
    },

    setDefaultCountryCode({ commit, state }, countryCode) {
      const effectiveCode = countryCode || state.workspaceDefaultCountryCode;
      commit("SET_DEFAULT_COUNTRY_CODE", effectiveCode);
      if (countryCode && countryCode !== state.workspaceDefaultCountryCode) {
        localStorage.setItem(USER_COUNTRY_CODE_KEY, countryCode);
      } else {
        localStorage.removeItem(USER_COUNTRY_CODE_KEY);
      }
    },
  },

  getters: {
    isPhoneNumberVerified: (state) => state.isPhoneNumberVerified,
    userTimezone: (state) => state.userTimezone,
    timezoneInitialized: (state) => state.timezoneInitialized,
    phoneNumbers: (state) => state.phoneNumbers,
    defaultCountryCode: (state) => state.defaultCountryCode,
    workspaceDefaultCountryCode: (state) => state.workspaceDefaultCountryCode,
    workspaceDefaultDialCode: (state) => state.workspaceDefaultDialCode,
    defaultCountryCodeInitialized: (state) => state.defaultCountryCodeInitialized,
  },
};
